import { useCallback, useEffect, useMemo, useState } from "react"

const getStorageKey = (userId) => `tyk-followed-campaigns:${userId || "anonymous"}`

const normalizeCampaign = (campaign) => ({
  id: Number(campaign.id),
  title: campaign.title || "Untitled campaign",
  proposal: campaign.proposal || "",
  groupId: campaign.groupId,
  groupName: campaign.groupName || `Group ${campaign.groupId}`,
  campaignType: campaign.campaignType || "Campaign",
  expiration: campaign.expiration || null,
  status: campaign.status || "",
  followedOn: campaign.followedOn || new Date().toISOString(),
})

export const useCampaignFollows = (userId) => {
  const storageKey = useMemo(() => getStorageKey(userId), [userId])
  const [followedCampaigns, setFollowedCampaigns] = useState([])

  const readFollows = useCallback(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "[]")
      setFollowedCampaigns(Array.isArray(parsed) ? parsed.filter((item) => item?.id) : [])
    } catch (error) {
      console.error("Error reading followed campaigns:", error)
      setFollowedCampaigns([])
    }
  }, [storageKey])

  useEffect(() => {
    readFollows()

    const handleStorage = (event) => {
      if (event.key === storageKey) {
        readFollows()
      }
    }

    const handleLocalUpdate = () => readFollows()

    window.addEventListener("storage", handleStorage)
    window.addEventListener("campaign-follows-updated", handleLocalUpdate)
    return () => {
      window.removeEventListener("storage", handleStorage)
      window.removeEventListener("campaign-follows-updated", handleLocalUpdate)
    }
  }, [readFollows, storageKey])

  const writeFollows = useCallback(
    (updater) => {
      setFollowedCampaigns((current) => {
        const next = updater(current)
        window.localStorage.setItem(storageKey, JSON.stringify(next))
        window.dispatchEvent(new Event("campaign-follows-updated"))
        return next
      })
    },
    [storageKey],
  )

  const isFollowingCampaign = useCallback(
    (campaignId) => followedCampaigns.some((campaign) => Number(campaign.id) === Number(campaignId)),
    [followedCampaigns],
  )

  const followCampaign = useCallback(
    (campaign) => {
      const normalized = normalizeCampaign(campaign)
      writeFollows((current) => {
        const withoutExisting = current.filter((item) => Number(item.id) !== normalized.id)
        return [normalized, ...withoutExisting].slice(0, 50)
      })
    },
    [writeFollows],
  )

  const unfollowCampaign = useCallback(
    (campaignId) => {
      writeFollows((current) => current.filter((campaign) => Number(campaign.id) !== Number(campaignId)))
    },
    [writeFollows],
  )

  const toggleCampaignFollow = useCallback(
    (campaign) => {
      if (isFollowingCampaign(campaign.id)) {
        unfollowCampaign(campaign.id)
        return false
      }

      followCampaign(campaign)
      return true
    },
    [followCampaign, isFollowingCampaign, unfollowCampaign],
  )

  return {
    followedCampaigns,
    followCampaign,
    unfollowCampaign,
    isFollowingCampaign,
    toggleCampaignFollow,
  }
}

export default useCampaignFollows
